import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Check, Circle, ExternalLink, Package, Printer, Wallet, Store, Truck, Unplug, KeyRound } from "lucide-react";
import { toast } from "sonner";

const EMPTY_ADDR = { name: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US" };

/** Ship-from address fields. Used on signup and in Settings. */
export const ShipFromFields = ({ value, onChange, compact = false, idPrefix = "shipfrom" }) => {
  const v = { ...EMPTY_ADDR, ...(value || {}) };
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  const h = compact ? "h-10" : "h-12";
  return (
    <div className="grid grid-cols-6 gap-2.5" data-testid={`${idPrefix}-fields`}>
      <div className="col-span-6"><Label className="text-xs text-muted-foreground">Full name</Label><Input required value={v.name} onChange={set("name")} placeholder="Who ships the package" className={`mt-1.5 ${h} rounded-xl`} data-testid={`${idPrefix}-name`} /></div>
      <div className="col-span-4"><Label className="text-xs text-muted-foreground">Street</Label><Input required value={v.street1} onChange={set("street1")} placeholder="100 Ocean Dr" className={`mt-1.5 ${h} rounded-xl`} data-testid={`${idPrefix}-street1`} /></div>
      <div className="col-span-2"><Label className="text-xs text-muted-foreground">Apt / unit</Label><Input value={v.street2 || ""} onChange={set("street2")} placeholder="4B" className={`mt-1.5 ${h} rounded-xl`} data-testid={`${idPrefix}-street2`} /></div>
      <div className="col-span-3"><Label className="text-xs text-muted-foreground">City</Label><Input required value={v.city} onChange={set("city")} placeholder="Miami Beach" className={`mt-1.5 ${h} rounded-xl`} data-testid={`${idPrefix}-city`} /></div>
      <div className="col-span-1"><Label className="text-xs text-muted-foreground">State</Label><Input required value={v.state} onChange={set("state")} placeholder="FL" maxLength={2} className={`mt-1.5 ${h} rounded-xl uppercase`} data-testid={`${idPrefix}-state`} /></div>
      <div className="col-span-2"><Label className="text-xs text-muted-foreground">ZIP</Label><Input required value={v.zip} onChange={set("zip")} placeholder="33139" className={`mt-1.5 ${h} rounded-xl font-mono-num`} data-testid={`${idPrefix}-zip`} /></div>
    </div>
  );
};

export const addressComplete = (a) => !!(a && a.name && a.street1 && a.city && a.state && a.zip);

const ProviderRow = ({ p, onConnect, onDisconnect, busy }) => (
  <div className="flex items-center justify-between gap-3 py-3 border-b border-border last:border-0" data-testid={`marketplace-${p.id}`}>
    <div className="flex items-center gap-3 min-w-0">
      <span className="h-9 w-9 rounded-xl bg-secondary grid place-items-center font-semibold text-sm">{p.name[0]}</span>
      <div className="min-w-0">
        <p className="text-sm font-medium leading-none">{p.name}</p>
        <p className="text-xs text-muted-foreground mt-1 truncate">
          {p.status === "connected" ? <>Connected{p.username ? ` as ${p.username}` : ""}</> : p.status === "coming_soon" ? "No public API yet - coming soon" : "List pieces with one tap"}
        </p>
      </div>
    </div>
    {p.status === "coming_soon" ? <Badge variant="secondary" className="rounded-full font-normal">Soon</Badge>
      : p.status === "connected" ? <Button size="sm" variant="outline" onClick={() => onDisconnect(p.id)} disabled={busy} className="rounded-full h-8" data-testid={`marketplace-${p.id}-disconnect`}><Unplug className="h-3.5 w-3.5 mr-1.5" /> Disconnect</Button>
        : <Button size="sm" onClick={() => onConnect(p.id)} disabled={busy} className="rounded-full h-8" data-testid={`marketplace-${p.id}-connect`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Connect"}</Button>}
  </div>
);

const StatusBadge = ({ s }) => {
  const map = { listed: ["Live", "bg-good/15 text-good"], sold: ["Sold - ship it", "bg-gold/20 text-gold-deep"], shipped: ["Shipped", "bg-secondary text-foreground"], ended: ["Ended", "bg-secondary text-muted-foreground"], paid_out: ["Paid out", "bg-secondary"] };
  const [label, cls] = map[s] || [s, "bg-secondary"];
  return <span className={`inline-flex items-center rounded-full px-2 h-6 text-[11px] font-medium ${cls}`}>{label}</span>;
};

/** Full Settings section: connections, ship-from, balance/cash-out, external listings, go-live checklist. */
export const MarketplacesPanel = () => {
  const [data, setData] = useState(null);
  const [listings, setListings] = useState([]);
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [busy, setBusy] = useState("");
  const [checklist, setChecklist] = useState(false);

  const load = async () => {
    try {
      const [d, l] = await Promise.all([api.marketplaces(), api.externalListings()]);
      setData(d); setListings(l.listings || []); if (d.ship_from) setAddr({ ...EMPTY_ADDR, ...d.ship_from });
    } catch { toast.error("Couldn't load marketplaces"); }
  };
  useEffect(() => { load(); }, []);

  const connect = async (id) => {
    setBusy(id);
    try {
      const r = await api.connectMarketplace(id);
      if (r.url) { window.location.href = r.url; return; }
      toast.success("eBay connected" + (r.mode === "mock" ? " (sandbox mock - paste real keys later)" : ""));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't connect"); }
    finally { setBusy(""); }
  };
  const disconnect = async (id) => { setBusy(id); try { await api.disconnectMarketplace(id); toast.success("Disconnected"); await load(); } catch { toast.error("Couldn't disconnect"); } finally { setBusy(""); } };
  const saveAddr = async (e) => {
    e?.preventDefault?.();
    if (!addressComplete(addr)) { toast.error("Fill in name, street, city, state and ZIP"); return; }
    setBusy("addr");
    try { await api.setShipFrom({ ...addr, state: addr.state.toUpperCase() }); toast.success("Ship-from address saved"); await load(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Couldn't save address"); }
    finally { setBusy(""); }
  };
  const simulate = async (l) => {
    setBusy(l.id);
    try { const r = await api.simulateSale(l.id); toast.success(`Sold for ${money(r.sale_price)} - label emailed to ${r.email?.to || "you"}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't simulate"); }
    finally { setBusy(""); }
  };
  const shipped = async (l) => { setBusy(l.id); try { await api.markShipped(l.id); toast.success("Marked shipped - funds released"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); } finally { setBusy(""); } };
  const end = async (l) => { setBusy(l.id); try { await api.endExternalListing(l.id); toast.success("Listing ended"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't end listing"); } finally { setBusy(""); } };
  const cashout = async () => {
    setBusy("cashout");
    try { const r = await api.cashout(); toast.success(`${money(r.cashout.amount)} on its way - arrives by ${r.cashout.eta}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nothing to cash out yet"); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="space-y-3"><Skeleton className="h-12 rounded-xl" /><Skeleton className="h-12 rounded-xl" /><Skeleton className="h-24 rounded-xl" /></div>;
  const bal = data.balance || {};
  const todo = (data.checklist || []).filter((c) => !c.done);
  const isMock = data.mode?.ebay === "mock";

  return (
    <div className="space-y-6" data-testid="marketplaces-panel">
      {/* balance */}
      <div className="rounded-2xl bg-foreground text-background p-5 flex flex-wrap items-end justify-between gap-4" data-testid="balance-card">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] text-background/60 inline-flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> Balance</p>
          <p className="font-display text-[40px] leading-none mt-2 font-mono-num" data-testid="balance-available">{money(bal.available || 0)}</p>
          <p className="text-xs text-background/70 mt-2">{bal.sales || 0} sold · {money(bal.gross || 0)} gross · {money(bal.fees || 0)} fees + labels{bal.paid_out ? ` · ${money(bal.paid_out)} paid out` : ""}</p>
        </div>
        <Button onClick={cashout} disabled={busy === "cashout" || !(bal.available > 0)} className="rounded-full h-11 px-5 bg-background text-foreground hover:bg-background/90" data-testid="cashout-button">
          {busy === "cashout" ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Cash out {bal.available > 0 ? money(bal.available) : ""}</>}
        </Button>
      </div>

      {/* connections */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-medium inline-flex items-center gap-1.5"><Store className="h-4 w-4" /> Connected marketplaces</p>
          {isMock && <button type="button" onClick={() => setChecklist(true)} className="text-[11px] inline-flex items-center gap-1 rounded-full bg-gold/15 text-gold-deep px-2.5 h-6 font-medium" data-testid="golive-badge"><KeyRound className="h-3 w-3" /> Sandbox mock · {todo.length} keys to go live</button>}
        </div>
        <p className="text-xs text-muted-foreground mb-2">List a piece on eBay in one tap. When it sells, we buy the label, email it to you, and your balance updates.</p>
        {data.providers.map((p) => <ProviderRow key={p.id} p={p} onConnect={connect} onDisconnect={disconnect} busy={busy === p.id} />)}
      </div>

      {/* ship from */}
      <form onSubmit={saveAddr} className="rounded-2xl border border-border p-4" data-testid="shipfrom-form">
        <p className="text-sm font-medium inline-flex items-center gap-1.5 mb-1"><Truck className="h-4 w-4" /> Ship-from address</p>
        <p className="text-xs text-muted-foreground mb-3">Printed on every label. {addressComplete(data.ship_from) ? <span className="text-good inline-flex items-center gap-1"><Check className="h-3 w-3" /> Saved</span> : <span className="text-gold-deep">Required before listing.</span>}</p>
        <ShipFromFields value={addr} onChange={setAddr} compact idPrefix="settings-shipfrom" />
        <Button type="submit" size="sm" variant="secondary" disabled={busy === "addr"} className="rounded-full mt-3" data-testid="settings-shipfrom-save">{busy === "addr" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save address"}</Button>
      </form>

      {/* external listings */}
      <div>
        <p className="text-sm font-medium inline-flex items-center gap-1.5 mb-2"><Package className="h-4 w-4" /> Listings on other marketplaces</p>
        {listings.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-5 text-center text-sm text-muted-foreground" data-testid="external-listings-empty">Nothing listed yet. Open any piece in your closet, draft the listing with AI, then tap <b className="text-foreground">List on eBay</b>.</div>
        ) : (
          <ul className="divide-y divide-border rounded-2xl border border-border" data-testid="external-listings">
            {listings.map((l) => (
              <li key={l.id} className="p-3.5 flex flex-wrap items-center gap-3" data-testid={`external-listing-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><p className="text-sm font-medium truncate">{l.title}</p><StatusBadge s={l.status} /></div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    eBay #{l.external_id} · {money(l.price)}
                    {l.status !== "listed" && l.sale_price != null && <> · sold {money(l.sale_price)} → <b className="text-foreground">{money(l.net)}</b> net</>}
                    {l.shipment?.tracking && <> · {l.shipment.carrier} {l.shipment.tracking}</>}
                  </p>
                  {l.email && <p className="text-[11px] text-muted-foreground mt-0.5">Label {l.email.mode === "live" ? "emailed" : "email (mock) logged"} to {l.email.to}</p>}
                </div>
                <div className="flex items-center gap-1.5">
                  <Button asChild size="sm" variant="ghost" className="rounded-full h-8 px-2.5"><a href={l.url} target="_blank" rel="noreferrer" data-testid={`external-listing-${l.id}-open`}><ExternalLink className="h-3.5 w-3.5" /></a></Button>
                  {l.status === "listed" && l.mode === "mock" && <Button size="sm" variant="outline" onClick={() => simulate(l)} disabled={busy === l.id} className="rounded-full h-8" data-testid={`external-listing-${l.id}-simulate`}>{busy === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Simulate sale"}</Button>}
                  {l.status === "listed" && <Button size="sm" variant="ghost" onClick={() => end(l)} disabled={busy === l.id} className="rounded-full h-8 text-muted-foreground" data-testid={`external-listing-${l.id}-end`}>End</Button>}
                  {l.shipment?.id && <Button asChild size="sm" variant="outline" className="rounded-full h-8"><a href={api.labelUrl(l.shipment.id)} target="_blank" rel="noreferrer" data-testid={`external-listing-${l.id}-label`}><Printer className="h-3.5 w-3.5 mr-1.5" /> Label</a></Button>}
                  {l.status === "sold" && <Button size="sm" onClick={() => shipped(l)} disabled={busy === l.id} className="rounded-full h-8" data-testid={`external-listing-${l.id}-shipped`}>Mark shipped</Button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={checklist} onOpenChange={setChecklist}>
        <DialogContent className="max-w-md" data-testid="golive-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Go-live checklist</DialogTitle><DialogDescription>Marketplace listing, labels and emails run in sandbox-mock mode until these keys are added to the backend <code className="font-mono-plex">.env</code>. The flow is identical; only the providers switch from mock to real.</DialogDescription></DialogHeader>
          <ul className="space-y-2.5 text-sm">
            {data.checklist.map((c) => (
              <li key={c.key} className="flex items-start gap-2.5" data-testid={`golive-${c.key}`}>
                {c.done ? <Check className="h-4 w-4 text-good mt-0.5" /> : <Circle className="h-4 w-4 text-muted-foreground mt-0.5" />}
                <div><p className="font-medium leading-tight">{c.label} <code className="text-[11px] text-muted-foreground font-mono-plex">{c.key}</code></p><p className="text-xs text-muted-foreground">{c.where}</p></div>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Compact eBay control used inside ListingControls on an item. */
export const ExternalListingButton = ({ item, onListed }) => {
  const [state, setState] = useState(null); // marketplaces overview
  const [busy, setBusy] = useState(false);
  const ext = (item.external_listings || []).find((e) => e.provider === "ebay");
  useEffect(() => { api.marketplaces().then(setState).catch(() => setState({ providers: [] })); }, [item.id]);
  const ebay = state?.providers?.find((p) => p.id === "ebay");
  const canList = item.listing_type === "sell" || item.listing_type === "both";
  const list = async () => {
    setBusy(true);
    try {
      const r = await api.listExternal(item.id, ["ebay"]);
      toast.success("Listed on eBay" + (r.listings?.[0]?.mode === "mock" ? " (sandbox mock)" : ""));
      onListed?.(r.listings?.[0]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't list on eBay"); }
    finally { setBusy(false); }
  };
  if (!state) return null;
  if (ext) {
    return (
      <a href={ext.url} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-lg bg-gray-25 border border-border px-3 h-9 text-xs" data-testid="ebay-listed-badge">
        <span className="inline-flex items-center gap-1.5 font-medium"><Store className="h-3.5 w-3.5" /> {ext.status === "sold" ? "Sold on eBay" : "Live on eBay"}</span>
        <span className="text-muted-foreground inline-flex items-center gap-1">View <ExternalLink className="h-3 w-3" /></span>
      </a>
    );
  }
  if (!ebay || ebay.status !== "connected") {
    return <a href="/settings#marketplaces" className="flex items-center justify-between rounded-lg border border-dashed border-border px-3 h-9 text-xs text-muted-foreground hover:text-foreground" data-testid="ebay-connect-link"><span className="inline-flex items-center gap-1.5"><Store className="h-3.5 w-3.5" /> Also sell on eBay</span><span>Connect →</span></a>;
  }
  return (
    <Button size="sm" variant="outline" onClick={list} disabled={busy || !canList || !item.listing_price} className="rounded-full w-full" title={!canList ? "Save it as For sale first" : ""} data-testid="ebay-list-button">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Store className="h-3.5 w-3.5 mr-1.5" /> List on eBay{item.listing_price ? ` · ${money(item.listing_price)}` : ""}</>}
    </Button>
  );
};
